import { useState, useCallback } from "react";

export interface AgeRange {
  id: string;
  from: number;
  to: number;
}

const DEFAULT_RANGES: AgeRange[] = [
  { id: "1", from: 0, to: 24 },
  { id: "2", from: 25, to: 49 },
  { id: "3", from: 50, to: 74 },
  { id: "4", from: 75, to: 100 },
];

export const useAgeRanges = () => {
  const [ranges, setRanges] = useState<AgeRange[]>(DEFAULT_RANGES);

  // Αυτόματος υπολογισμός ίσων διαστημάτων από 0 έως 100 για N γραμμές
  const recalculateRanges = useCallback((count: number): AgeRange[] => {
    const maxAge = 100;
    const step = Math.floor(maxAge / count);
    const newRanges: AgeRange[] = [];

    let currentFrom = 0;
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      const currentTo = isLast ? maxAge : currentFrom + step - 1;
      
      newRanges.push({
        id: String(Date.now() + i),
        from: currentFrom,
        to: currentTo,
      });

      currentFrom = currentTo + 1;
    }
    return newRanges;
  }, []);

  // Προσθήκη νέας γραμμής & ανακατανομή
  const addRange = useCallback(() => {
    setRanges((prev) => recalculateRanges(prev.length + 1));
  }, [recalculateRanges]);

  // Αφαίρεση γραμμής & ανακατανομή (ελάχιστο 1 γραμμή)
  const removeRange = useCallback((id: string) => {
    setRanges((prev) => {
      if (prev.length <= 1) return prev;
      return recalculateRanges(prev.length - 1);
    });
  }, [recalculateRanges]);

  // Χειροκίνητη αλλαγή τιμής από τον χρήστη
  const updateRange = useCallback((id: string, field: "from" | "to", value: number) => {
    setRanges((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: Math.max(0, value) } : r))
    );
  }, []);

  // Επαναφορά στα προεπιλεγμένα 4 διαστήματα
  const resetRanges = useCallback(() => {
    setRanges(DEFAULT_RANGES);
  }, []);

  return {
    ranges,
    addRange,
    removeRange,
    updateRange,
    resetRanges,
  };
};

/**
 * Utility Function: Υπολογίζει την ηλικία από το έτος γέννησης
 * και επιστρέφει το Label του διαστήματος στο οποίο ανήκει ο ασθενής.
 */
export const getAgeGroupLabel = (
  birthDateStr: string | null | undefined,
  ranges: AgeRange[]
): string => {
  if (!birthDateStr) return "Άγνωστη Ηλικία";

  // Παίρνουμε μόνο τη χρονιά (π.χ. από "1985-05-20" ή "1985/05/20")
  const birthYear = new Date(birthDateStr).getFullYear();
  if (isNaN(birthYear)) return "Άγνωστη Ηλικία";

  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear + 1;

  // Βρίσκουμε σε ποιο εύρος ανήκει
  const matchedRange = ranges.find((r) => age >= r.from && age <= r.to);

  if (matchedRange) {
    return `${matchedRange.from} - ${matchedRange.to} ετών`;
  }

  return age > 100 ? "> 100 ετών" : "Εκτός διαστήματος";
};